import { Injectable, inject, signal } from '@angular/core';
import { type Schema } from '../../../../amplify/data/resource';
import { generateClient } from 'aws-amplify/api';
import { User, UserSettings } from '../models/user.model';
import { BehaviorSubject } from 'rxjs';
import { getCurrentUser } from 'aws-amplify/auth';
import { map } from 'rxjs/operators';
import { GoogleCalendarService } from './google-calendar.service';


@Injectable({
  providedIn: 'root',
})
export class UserService {
  private _client?: ReturnType<typeof generateClient<Schema>>;

  private get client() {
    if (!this._client) {
      this._client = generateClient<Schema>();
    }
    return this._client;
  }

  private usersSubject = new BehaviorSubject<User[]>([]);
  /**
   * Every user, archived ones included. Use this when you need to *read*
   * historical data (reporting, name resolution on old records).
   */
  users$ = this.usersSubject.asObservable();

  /**
   * Only users who are still active. Use this for anything that *assigns*
   * work (task/todo assignees, project members and managers).
   */
  activeUsers$ = this.users$.pipe(
    map(users => users.filter(u => !u.isArchived))
  );

  /** Synchronous snapshot of all users, archived included (for ID resolution). */
  getUsersSnapshot(): User[] {
    return this.usersSubject.value;
  }

  /** Synchronous snapshot of the active (non-archived) users. */
  getActiveUsersSnapshot(): User[] {
    return this.usersSubject.value.filter(u => !u.isArchived);
  }

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  currentUser$ = this.currentUserSubject.asObservable();

  /** Synchronous snapshot of the current user (for imperative code). */
  getCurrentUserSnapshot(): User | null {
    return this.currentUserSubject.value;
  }

  private settingsSubject = new BehaviorSubject<UserSettings | null>(null);
  settings$ = this.settingsSubject.asObservable();

  private currentSettingsId: string | null = null;

  // Signal pour l'admin status - plus réactif que l'observable
  private _isAdmin = signal<boolean>(false);
  isAdmin = this._isAdmin.asReadonly();

  // Cache for tenant users to avoid redundant API calls
  private tenantUsersCache = new Map<string, { users: User[], timestamp: number }>();
  private readonly CACHE_TTL_MS = 60000; // Cache for 1 minute

  private googleCalendar = inject(GoogleCalendarService);

  constructor(){
    // Don't call init() here - it will be called explicitly when needed (e.g., after sign in)
    // Calling it here may happen before Amplify is configured
  }


  isAdmin$ = this.currentUser$.pipe(
    map(user => user?.role === 'SUPERADMIN' || user?.role === 'ADMIN')
  );

  private _initialized = false;

  async init() {
    if (this._initialized) return;
    this._initialized = true;
    // Start real-time observers first
    this.startRealTimeObservers();

    // Initial load
    await this.listUsers();
    await this.loadCurrentUser();
  }

  // Start real-time observers using Amplify's observeQuery
  startRealTimeObservers() {
    // Observe all users in real-time
    this.client.models.User.observeQuery({
      selectionSet: ['id', 'cognitoId', 'name', 'firstName', 'email', 'phone', 'role', 'isArchived', 'archivedAt', 'tenants.id', 'tenants.tenantId', 'tenants.tenant.id', 'tenants.tenant.name']
    }).subscribe({
      next: ({ items, isSynced }) => {
        const users = items.map(usr => ({
          id: usr.id,
          cognitoId: usr.cognitoId,
          name: usr.name,
          firstName: usr.firstName,
          email: usr.email,
          phone: usr.phone,
          role: usr.role,
          isArchived: usr.isArchived ?? false,
          archivedAt: usr.archivedAt,
          tenants: usr.tenants?.map(ut => ({
            id: ut.id,
            tenantId: ut.tenantId,
            userId: usr.id,
            tenant: ut.tenant ? { id: ut.tenant.id, name: ut.tenant.name } : undefined
          }))
        })) as User[];

        this.usersSubject.next(users);

        if (isSynced) {
          console.log('✅ [Users] Real-time sync completed');
        }
      },
      error: (error) => console.error('❌ [Users] Real-time subscription error:', error)
    });
  }

  // Stop real-time updates (no-op with observeQuery)
  stopRealTimeRefresh() {
    // Subscriptions are managed automatically by observeQuery
  }

  async listUsers() {

    console.log("Listing users - checking client and models availability", {
        clientAvailable: !!this.client,
        modelsAvailable: !!this.client?.models,
        userModelAvailable: !!this.client?.models?.User
    })
    try{
      // Defensive check - ensure client and models are available
      if (!this.client) {
        console.error("Amplify client is not available");
        return;
      }

      if (!this.client.models) {
        console.error("Amplify client models are not available. Amplify may not be configured yet.");
        return;
      }

      if (!this.client.models.User) {
        console.error("User model is not available on Amplify client");
        return;
      }

      const { data, errors } = await this.client.models.User.list({
        selectionSet: ['id', 'cognitoId', 'name', 'firstName', 'email', 'phone', 'role', 'isArchived', 'archivedAt', 'tenants.id', 'tenants.tenantId', 'tenants.tenant.id', 'tenants.tenant.name']
      });
      if (errors && errors.length) console.error("Error while getting User", errors);
      const users = data.map(usr => ({
        id: usr.id,
        cognitoId: usr.cognitoId,
        name: usr.name,
        firstName: usr.firstName,
        email: usr.email,
        phone: usr.phone,
        role: usr.role,
        isArchived: usr.isArchived ?? false,
        archivedAt: usr.archivedAt,
        tenants: usr.tenants?.map(ut => ({
          id: ut.id,
          tenantId: ut.tenantId,
          userId: usr.id,
          tenant: ut.tenant ? { id: ut.tenant.id, name: ut.tenant.name } : undefined
        }))
      })) as User[];
      this.usersSubject.next(users);
    } catch (e) {
      console.error("Failed to get all users", e);
    }
  }

  // Get users filtered by tenantId (server-side filtering)
  async getUsersByTenantId(tenantId: string): Promise<User[]> {
    // Check cache first
    const cached = this.tenantUsersCache.get(tenantId);
    const now = Date.now();

    if (cached && (now - cached.timestamp) < this.CACHE_TTL_MS) {
      console.log(`💾 [getUsersByTenantId] Using cached data for tenant ${tenantId} (${cached.users.length} users)`);
      return cached.users;
    }

    try {
      console.log(`🌐 [getUsersByTenantId] Fetching users from server for tenant ${tenantId}`);

      // Use the secondary index query field for better performance
      const { data: userTenants, errors } = await this.client.models.UserTenant.listUserTenantsByTenantId({
        tenantId: tenantId
      }, {
        selectionSet: ['id', 'userId', 'tenantId', 'user.id', 'user.cognitoId', 'user.name', 'user.firstName', 'user.email', 'user.phone', 'user.role', 'user.isArchived', 'user.archivedAt', 'tenant.id', 'tenant.name']
      });

      if (errors && errors.length) {
        console.error("Error while getting users by tenant", errors);
        return [];
      }

      console.log(`📋 [getUsersByTenantId] Found ${userTenants.length} users for tenant ${tenantId}`);

      // Map to User objects
      const users = userTenants
        .filter(ut => ut.user) // Filter out entries without user
        .map(ut => ({
          id: ut.user!.id,
          cognitoId: ut.user!.cognitoId,
          name: ut.user!.name,
          firstName: ut.user!.firstName,
          email: ut.user!.email,
          phone: ut.user!.phone,
          role: ut.user!.role,
          isArchived: ut.user!.isArchived ?? false,
          archivedAt: ut.user!.archivedAt,
          tenants: [{
            id: ut.id,
            tenantId: ut.tenantId,
            userId: ut.user!.id,
            tenant: ut.tenant ? { id: ut.tenant.id, name: ut.tenant.name } : undefined
          }]
        })) as User[];

      // Update cache
      this.tenantUsersCache.set(tenantId, { users, timestamp: now });
      console.log(`💾 [getUsersByTenantId] Cached ${users.length} users for tenant ${tenantId}`);

      return users;
    } catch (e) {
      console.error("Failed to get users by tenant", e);
      return [];
    }
  }

  /**
   * Same as getUsersByTenantId but without the archived users — for pickers.
   */
  async getActiveUsersByTenantId(tenantId: string): Promise<User[]> {
    const users = await this.getUsersByTenantId(tenantId);
    return users.filter(u => !u.isArchived);
  }

  async loadCurrentUser() {
    try {
      const authUser = await getCurrentUser();
      const cognitoId = authUser.userId;
      const { data: users, errors } = await this.client.models.User.list({
        filter: { cognitoId: { eq: cognitoId } },
        selectionSet: ['id', 'cognitoId', 'name', 'firstName', 'email', 'phone', 'role', 'isArchived', 'archivedAt', 'tenants.id', 'tenants.tenantId', 'tenants.tenant.id', 'tenants.tenant.name']
      });
      if (errors) console.error("Error loading current user", errors);
      const userData = users.length > 0 ? users[0] : null;
      const current = userData ? {
        id: userData.id,
        cognitoId: userData.cognitoId,
        name: userData.name,
        firstName: userData.firstName,
        email: userData.email,
        phone: userData.phone,
        role: userData.role,
        isArchived: userData.isArchived ?? false,
        archivedAt: userData.archivedAt,
        tenants: userData.tenants?.map(ut => ({
          id: ut.id,
          tenantId: ut.tenantId,
          userId: userData.id,
          tenant: ut.tenant ? { id: ut.tenant.id, name: ut.tenant.name } : undefined
        }))
      } as User : null;
      this.currentUserSubject.next(current);
      // Mettre à jour le signal isAdmin
      this._isAdmin.set(current?.role === 'SUPERADMIN' || current?.role === 'ADMIN');
      if (current && current.id) await this.loadSettingsForUser(current.id);
    } catch (err) {
      console.error("Failed to load current user", err);
      this.currentUserSubject.next(null);
      this._isAdmin.set(false);
    }
  }

  private async loadSettingsForUser(userId: string) {
    try {
      const { data: settingsList } = await this.client.models.UserSettings.list({
        filter: { userId: { eq: userId } }
      });
      if (settingsList.length > 0) {
        const settings = settingsList[0] as unknown as UserSettings;
        this.currentSettingsId = settings.id;
        this.settingsSubject.next(settings);
        // Initialise the Google Calendar service for every page load,
        // not just when the settings component is visited
        this.googleCalendar.init(settings.id).catch(() => {});
      }
    } catch (e) {
      console.error('Error loading settings', e);
    }
  }

  async updateUserSettings(updates: Partial<UserSettings>) {
    if (this.currentSettingsId) {
      try {
        const { data, errors } = await this.client.models.UserSettings.update({
          id: this.currentSettingsId,
          ...updates
        });
        if (errors) throw new Error(errors[0].message);
        const updatedSettings = data as unknown as UserSettings;
        const current = this.settingsSubject.value;
        if (current) this.settingsSubject.next({ ...current, ...updates, ...updatedSettings });
      } catch (e) {
        console.error("Failed to update settings", e);
        throw e;
      }
    }
    else {
      const currentUser = this.currentUserSubject.value;
      if (!currentUser || !currentUser.id) {
        console.error("Cannot create settings: No current user loaded");
        return;
      }
      try {
        const newSettingsInput: any = {
          ...updates,
          userId: currentUser.id,
          displayModeForProject: updates.displayModeForProject ?? "LIST",
          secondsToAvoid: updates.secondsToAvoid ?? "5"
        };
        const { data, errors } = await this.client.models.UserSettings.create(newSettingsInput);
        if (errors) throw new Error(errors[0].message);
        const newSettings = data as unknown as UserSettings;
        this.currentSettingsId = newSettings.id;
        this.settingsSubject.next(newSettings);
        console.log("Settings created successfully for the first time");
      } catch (e) {
        console.error("Failed to create settings", e);
        throw e;
      }
    }
  }

  async updateCurrentUser(updates: Partial<User>) {
    const current = this.currentUserSubject.value;
    if (!current || !current.id) return;
    try {
      const { data, errors } = await this.client.models.User.update({
        id: current.id,
        ...updates
      });
      if (errors) throw new Error(errors[0].message);
      this.currentUserSubject.next({ ...current, ...data } as User);
    } catch (e) {
      console.error("Failed to update profile", e);
      throw e;
    }
  }

  async updateUserRole(userId: string, role: 'SUPERADMIN' | 'ADMIN' | 'EMPLOYEE') {
    const { data, errors } = await this.client.mutations.updateUserRole({
      userId,
      role
    });
    if (errors && errors.length) {
      console.error("Error updating user role", errors);
      throw new Error(errors[0].message);
    }
    return data;
  }

  async createUser(user: User, tenantId: string, tempPassword: string) {
    const { data, errors } = await this.client.mutations.createUserCustom({
      name: user.name,
      firstName: user.firstName,
      email: user.email || "",
      phone: user.phone ? user.phone : undefined,
      tenantId: tenantId,
      role: user.role,
      tempPassword: tempPassword
    });
    if (errors) {
      console.error("Error create : ", errors);
      throw new Error(errors[0].message);
    }
    return JSON.parse(data || '{}');
  }

  async deleteUser(userId: string) {
    const { data, errors } = await this.client.mutations.deleteUserCustom({
        id: userId
    });
    if (errors) {
        console.error('Errors deleting user', errors);
        throw new Error(errors[0].message);
    }
    return data;
  }

  /**
   * Archive a user: keeps every record (time entries, tasks, comments) so admins
   * can still consult the history, but disables the Cognito account.
   */
  async archiveUser(userId: string) {
    return this.setUserArchived(userId, true);
  }

  /** Restore an archived user and re-enable the Cognito account. */
  async restoreUser(userId: string) {
    return this.setUserArchived(userId, false);
  }

  private async setUserArchived(userId: string, archived: boolean) {
    const { data, errors } = await this.client.mutations.archiveUserCustom({
      id: userId,
      archived
    });
    if (errors && errors.length) {
      console.error(`Error ${archived ? 'archiving' : 'restoring'} user`, errors);
      throw new Error(errors[0].message);
    }
    // The lambda writes straight to DynamoDB, so observeQuery won't emit:
    // refresh the list ourselves.
    this.clearTenantCache();
    await this.listUsers();
    return data;
  }

  // Tenant management for users
  async addUserToTenant(userId: string, tenantId: string) {
    try {
      const { data, errors } = await this.client.models.UserTenant.create({
        userId: userId,
        tenantId: tenantId
      });
      if (errors && errors.length) {
        console.error('Error adding user to tenant', errors);
        throw new Error(errors[0].message);
      }
      // Invalidate cache for this tenant
      this.tenantUsersCache.delete(tenantId);
      console.log(`🗑️ [Cache] Invalidated cache for tenant ${tenantId} after adding user`);

      // Refresh user list to get updated tenant associations
      await this.listUsers();
      return data;
    } catch (e) {
      console.error('Failed to add user to tenant', e);
      throw e;
    }
  }

  async removeUserFromTenant(userTenantId: string, tenantId?: string) {
    try {
      const { data, errors } = await this.client.models.UserTenant.delete({
        id: userTenantId
      });
      if (errors && errors.length) {
        console.error('Error removing user from tenant', errors);
        throw new Error(errors[0].message);
      }
      // Invalidate cache if tenantId is provided
      if (tenantId) {
        this.tenantUsersCache.delete(tenantId);
        console.log(`🗑️ [Cache] Invalidated cache for tenant ${tenantId} after removing user`);
      }

      // Refresh user list to get updated tenant associations
      await this.listUsers();
      return data;
    } catch (e) {
      console.error('Failed to remove user from tenant', e);
      throw e;
    }
  }

  // Clear all tenant cache (useful when data changes)
  clearTenantCache() {
    this.tenantUsersCache.clear();
    console.log(`🗑️ [Cache] Cleared all tenant user cache`);
  }

  resetState() {
    this.currentUserSubject.next(null);
    this.settingsSubject.next(null);
    this.currentSettingsId = null;
    this.usersSubject.next([]);
    this._isAdmin.set(false);
    this._initialized = false;
  }
}
